import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, query, where, getDocs, orderBy, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import type { Process } from '@/types';

export function ProcessesPage() {
  const { appUser, isOwner, isAdmin, canWriteCompanyData } = useAuth();
  const [processes, setProcesses] = useState<Process[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const canDeleteProcess = isOwner || isAdmin;

  const loadProcesses = useCallback(async () => {
    if (!appUser) {
      setLoading(false);
      setProcesses([]);
      return;
    }
    setLoading(true);
    setError(null);
    const mapDocs = (docs: { id: string; data: () => Record<string, unknown> }[]) =>
      docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: (d.data().createdAt as string) ?? '',
          updatedAt: (d.data().updatedAt as string) ?? '',
        })) as Process[];

    try {
      const col = collection(db, 'processes');
      const q = isOwner
        ? query(col, orderBy('createdAt', 'desc'))
        : query(col, where('companyId', '==', appUser.companyId), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setProcesses(mapDocs(snap.docs));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const needsIndex = /index|índice/i.test(msg);
      if (needsIndex) {
        try {
          const col = collection(db, 'processes');
          const qSimple = isOwner
            ? col
            : query(col, where('companyId', '==', appUser.companyId));
          const snap = await getDocs(qSimple);
          const list = mapDocs(snap.docs);
          list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
          setProcesses(list);
          setError(null);
        } catch (fallbackErr) {
          console.error('Error cargando procesos', fallbackErr);
          setError(msg);
        }
      } else {
        console.error('Error cargando procesos', err);
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [appUser, isOwner]);

  useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  const handleDelete = async (p: Process) => {
    if (!canDeleteProcess) return;
    if (!window.confirm(`¿Eliminar el proceso "${p.title}"? Esta acción no se puede deshacer.`)) return;
    setDeletingId(p.id);
    try {
      await deleteDoc(doc(db, 'processes', p.id));
      setProcesses((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      console.error('Error al eliminar proceso', err);
      setError(err instanceof Error ? err.message : 'Error al eliminar el proceso');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Procesos</h1>
        {canWriteCompanyData && (
          <Button asChild>
            <Link to="/app/processes/new">Nuevo proceso</Link>
          </Button>
        )}
      </div>
      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <p>{error}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={loadProcesses}>
            Reintentar
          </Button>
        </div>
      )}
      {loading ? (
        <p className="text-muted-foreground">Cargando...</p>
      ) : processes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">No hay procesos. Crea uno para empezar.</p>
            {canWriteCompanyData && (
              <Button asChild className="mt-4">
                <Link to="/app/processes/new">Crear proceso</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {processes.map((p) => (
            <Card key={p.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  <Link to={`/app/processes/${p.id}`} className="hover:underline">
                    {p.title}
                  </Link>
                </CardTitle>
                <span
                  className={`inline-block w-fit rounded-full px-2 py-0.5 text-xs ${
                    p.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : p.status === 'closed'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {p.status === 'draft' ? 'Borrador' : p.status === 'active' ? 'Activo' : 'Cerrado'}
                </span>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/app/processes/${p.id}`}>Ver</Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/app/processes/${p.id}/candidates`}>Candidatos</Link>
                </Button>
                {canDeleteProcess && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-destructive/50 text-destructive hover:bg-destructive/10"
                    disabled={deletingId === p.id}
                    onClick={() => handleDelete(p)}
                  >
                    {deletingId === p.id ? 'Eliminando…' : 'Eliminar'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
